import { createFileRoute } from "@tanstack/react-router";
import { EnCard } from "@/components/en/EnCard";
import { SectionHeading, BriefingCTA } from "@/components/en/EnPrimitives";
import { ideas } from "@/content/en/ideas";
import { concepts } from "@/content/en/concepts";
import { hreflangLinks } from "@/lib/seo";
import { pageTitle } from "@/lib/brand";

const CANONICAL = "https://smartsustainability.ai/ideas";
const TITLE = pageTitle("Ideas", "en");
const DESCRIPTION =
  "Long-form essays and analytical perspectives on AI, sustainability, society, and governance.";

export const Route = createFileRoute("/ideas/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:locale", content: "en_US" },
      { property: "og:locale:alternate", content: "tr_TR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/yazilar", "/ideas"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESCRIPTION,
          url: CANONICAL,
          inLanguage: "en",
          hasPart: ideas.map((i) => ({
            "@type": "Article",
            headline: i.title,
            description: i.fiveSecondAnswer,
            url: `${CANONICAL}/${i.slug}`,
            author: { "@type": "Person", name: "Arda Öztaşkın" },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://smartsustainability.ai" },
            { "@type": "ListItem", position: 2, name: "Ideas", item: CANONICAL },
          ],
        }),
      },
    ],
  }),
  component: IdeasIndex,
});


function IdeasIndex() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
      <SectionHeading as="h1" kicker="Section" title="Ideas">
        Long-form essays and analytical perspectives on AI, sustainability,
        society, and governance. Written for a global reader, not translated
        from the Turkish site.
      </SectionHeading>

      <div className="grid gap-6 md:grid-cols-2">
        {ideas.map((i) => (
          <EnCard
            key={i.slug}
            type="Idea"
            share
            href={`/ideas/${i.slug}`}
            title={i.title}
            summary={i.fiveSecondAnswer}
            topics={i.topics}
            meta={`${i.readingMinutes} min read`}
            tag={i.relatedConcepts[0]
              ? concepts.find((c) => c.slug === i.relatedConcepts[0])?.name
              : undefined}
          />
        ))}
      </div>

      <BriefingCTA />
    </div>
  );
}
